import { ChangeEvent, useState } from 'react';
import Link from 'next/link';
import Image from 'next/image';
import Container from '../Container';
import { H4, H6, P } from '../Text';
import { SolidButton } from '../Button';
import CustomInput from '../CustomInput';
import MailIcon from 'assets/svgs/MailIcon';
import logoNegative from 'assets/images/logo-negative.png';
import AllmylesLogo from 'assets/svgs/AllmylesLogo';
import { useTranslation } from 'next-i18next/pages';
import {
  CautionModal,
  LoginSignupModal,
  LoginSignupModalType,
} from 'components/Modal';
import {
  successIllustration as sucessfulPurchaseIcon,
  failureIllustration as unSucessfulPurchaseIcon,
} from 'assets/illustrations';
import { useRouter } from 'next/router';
import { findKeyByRewriteUrl } from 'urlTranslations';
import { FAVOURITES_PATH } from 'utils/favouriteHotels';
import { PACKAGE_TOURS_IN_HOUSE } from 'utils/packageToursSwitch';
import { IS_ALLMYLES, brandConfig } from 'utils/brand';
import { openCookieSettings } from 'hooks/useCookieConsent';

interface IFooterProps {
  newsLetterRef: any;
}
interface SubscribeUserProps {
  success: boolean;
  error: string;
}

const Footer: React.FC<IFooterProps> = ({ newsLetterRef }) => {
  const [email, setEmail] = useState<string>('');
  const [successModal, setSuccessModal] = useState<boolean>(false);
  const [unsuccessModal, setUnsuccessModal] = useState<boolean>(false);
  const [signupModalOpen, setSignupModalOpen] = useState<boolean>(false);
  const [error, setError] = useState<string>('');
  const { t } = useTranslation();
  const router = useRouter();
  const date = new Date();
  // Shown twice: in the bottom bar on desktop, at the end of the columns on
  // mobile.
  const legalLinks = [
    {
      label: 'Terms of Use',
      href: findKeyByRewriteUrl('/terms-and-conditions/', router.locale),
    },
    {
      label: 'Privacy policy',
      href: findKeyByRewriteUrl('/privacy-policy/', router.locale),
    },
    {
      label: 'Powered by Allmyles',
      href: 'https://allmyles.com/',
      external: true,
    },
  ];
  const socialLinks = [
    { name: 'Facebook', href: process.env.NEXT_PUBLIC_FACEBOOK_URL },
    { name: 'Instagram', href: process.env.NEXT_PUBLIC_INSTAGRAM_URL },
    { name: 'Twitter', href: process.env.NEXT_PUBLIC_TWITTER_URL },
    { name: 'LinkedIn', href: process.env.NEXT_PUBLIC_LINKEDIN_URL },
  ].filter(
    // A build without a profile URL would otherwise link to "undefined".
    (link): link is { name: string; href: string } => Boolean(link.href),
  );
  const closeModalSuccessModal = () => setSuccessModal(false);
  const closeUnModalSuccessModal = () => setUnsuccessModal(false);
  const subscribeUser = async (e: any) => {
    e.preventDefault();

    const resp = await fetch('/api/subscribeUser', {
      body: JSON.stringify({
        email: email,
      }),

      headers: {
        'Content-Type': 'application/json',
      },

      method: 'POST',
    });

    const res = await resp.text();
    const parsed: SubscribeUserProps = JSON.parse(res);
    if (parsed.success) {
      setSuccessModal(true);
    } else {
      setUnsuccessModal(true);
      setError(parsed.error);
    }
  };

  return (
    <div id="footer" className="mt-auto bg-red-100">
      <Container containerClassName="bg-white" className="tt-footer md:gap-8">
        <LoginSignupModal
          closeModal={() => setSignupModalOpen(false)}
          modalType={LoginSignupModalType.SIGNUP}
          modalOpen={signupModalOpen}
        />
        <CautionModal
          closeModal={closeModalSuccessModal}
          title={t('Successfully subscribed to newsletter')}
          description=""
          icon={
            <Image
              src={sucessfulPurchaseIcon}
              alt=""
              className="w-1/2 h-1/2 mx-auto mb-4"
            />
          }
          btn1Text={t('Close')}
          modalOpen={successModal}
        />
        <CautionModal
          closeModal={closeUnModalSuccessModal}
          title={t('Could not subscribe to newsletter')}
          description={t(error)}
          icon={
            <Image
              src={unSucessfulPurchaseIcon}
              alt=""
              className="w-1/2 h-1/2 mx-auto mb-4"
            />
          }
          btn1Text={t('Close')}
          modalOpen={unsuccessModal}
        />
        <div className="tt-footer-div">
          {/* The brand's name (WHIT-493); Tipptour keeps its translated
              title. */}
          <H4 as="h2" bolded className="mb-1 2xl:mb-4">
            {IS_ALLMYLES ? brandConfig.name : t('Tipptour')}
          </H4>
          <P>
            <Link href={findKeyByRewriteUrl('/about-us/', router.locale)}>
              {t('About us')}
            </Link>
          </P>
          <P>
            <Link href={findKeyByRewriteUrl('/jobs/', router.locale)}>
              {t('Careers')}
            </Link>
          </P>
          <P>
            <Link href={findKeyByRewriteUrl('/contact/', router.locale)}>
              {t('Contact')}
            </Link>
          </P>
          <P>
            <Link href={findKeyByRewriteUrl('/quote-request/', router.locale)}>
              {t('Quote request')}
            </Link>
          </P>
        </div>
        <div className="tt-footer-div 2xl:pl-2">
          <H4 as="h2" bolded className="mb-1 2xl:mb-4">
            {t('Your trip')}
          </H4>
          <P>
            <Link href={findKeyByRewriteUrl('/bookings/', router.locale)}>
              {t('My bookings')}
            </Link>
          </P>
          {PACKAGE_TOURS_IN_HOUSE && (
            <P>
              <Link href={findKeyByRewriteUrl(FAVOURITES_PATH, router.locale)}>
                {t('Favourite hotels')}
              </Link>
            </P>
          )}
          <P>
            <button
              className="md:text-left"
              onClick={() => setSignupModalOpen(true)}>
              {t('Sign in or register')}
            </button>
          </P>
          <P>
            <Link href={findKeyByRewriteUrl('/faq/', router.locale)}>
              {t('F.A.Q')}
            </Link>
          </P>
          <P>
            <Link href={findKeyByRewriteUrl('/contact/', router.locale)}>
              {t('Help and support')}
            </Link>
          </P>
        </div>
        <div className="tt-footer-div 2xl:pl-4">
          <H4 as="h2" bolded className="mb-1 2xl:mb-4">
            {t('Travel ideas')}
          </H4>
          <P>
            <Link
              scroll={false}
              href={`${router.pathname === '/' ? '#search-flights' : '/'}`}>
              {t('Search flights')}
            </Link>
          </P>
          <P>
            {/* The posts page has no translated slug; Link keeps the locale
                prefix, so /hu/posts/ is served as it is. */}
            <Link href="/posts/">{t('Travel tips')}</Link>
          </P>
        </div>
        <div className="tt-footer-newsletter-div w-full 2xl:pl-6">
          <H4 as="h2" bolded className="mb-1">
            {t('Newsletter')}
          </H4>
          <P className="2xl:mt-3.5">
            {t(
              'Sign up to our newsletter to become informed about the best deals instantly.',
            )}
          </P>
          <div className="tt-footer-newsletter-input">
            <CustomInput
              id="newsletter"
              name="newsletter"
              newsLetterRef={newsLetterRef}
              containerClassName="w-full md:w-auto md:flex-[3_3_0%] h-12"
              className="pl-12 py-3 2xl:w-[22.5rem] 2xL:h-[12rem]"
              textPadding="h-full"
              icon={
                <MailIcon
                  className="w-6 h-6"
                  mainColor="var(--tt-light-purple)"
                  secondaryColor="var(--tt-light-purple)"
                />
              }
              value={email}
              onChange={(e: ChangeEvent<HTMLInputElement>) => {
                setEmail(e.target.value);
              }}
              placeholder={t('Email address') || ''}
              type="email"
            />
            <SolidButton
              onClick={subscribeUser}
              className="md:flex-1 md:px-5 2xl:px-0 2xl:px-3.5">
              {t('Subscribe')}
            </SolidButton>
          </div>
          {socialLinks.length > 0 && (
            <div className="mt-6">
              <H4 as="h2" bolded className="mb-2">
                {t('Follow us')}
              </H4>
              <div className="flex flex-wrap justify-center md:justify-start gap-x-5 gap-y-2">
                {socialLinks.map(({ name, href }) => (
                  <P key={name}>
                    <Link href={href} target="_blank" rel="noreferrer">
                      {name}
                    </Link>
                  </P>
                ))}
              </div>
            </div>
          )}
        </div>
        <div className="tt-footer-div md:hidden !mb-0">
          {legalLinks.map(({ label, href, external }) => (
            <P key={label} className="text-tt-dark-purple">
              <Link
                href={href}
                {...(external ? { target: '_blank', rel: 'noreferrer' } : {})}>
                {t(label)}
              </Link>
            </P>
          ))}
          <P className="text-tt-dark-purple">
            <button type="button" onClick={openCookieSettings}>
              {t('Cookie settings')}
            </button>
          </P>
        </div>
      </Container>
      <Container
        containerClassName="bg-tt-verydark-purple md:min-h-24 flex items-center"
        className="flex items-center py-4 w-full">
        {/* Same brand as the navbar: Tipptour on production, Allmyles on the
            showcase (WHIT-410, WHIT-493). */}
        <Link href="/" passHref aria-label={brandConfig.name}>
          {IS_ALLMYLES ? (
            <AllmylesLogo variant="light" className="w-[8.75rem] h-8" />
          ) : (
            <Image
              style={{ color: undefined }}
              src={logoNegative}
              className="w-[8.75rem] h-8 cursor-pointer"
              alt=""
            />
          )}
        </Link>
        {/* Each link on one line; when the row is too narrow, whole links
            wrap to a second row instead of each breaking in two, and the
            bar grows to hold them (WHIT-479). */}
        <div className="hidden md:flex flex-initial flex-wrap ml-[3.75rem] gap-x-6 gap-y-2">
          {legalLinks.map(({ label, href, external }) => (
            <P key={label} className="text-white whitespace-nowrap">
              <Link
                href={href}
                {...(external ? { target: '_blank', rel: 'noreferrer' } : {})}>
                {t(label)}
              </Link>
            </P>
          ))}
          <P className="text-white whitespace-nowrap">
            <button type="button" onClick={openCookieSettings}>
              {t('Cookie settings')}
            </button>
          </P>
        </div>
        <H6
          as="p"
          className="flex-1 md:min-w-[10rem] ml-6 lg:min-w-max lg:whitespace-nowrap text-white text-right">
          {date.getFullYear()} - {t('All rights reserved')}{' '}
          {process.env.NEXT_PUBLIC_APPLICATION_NAME}
        </H6>
      </Container>
    </div>
  );
};

export default Footer;
